import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SocialSignInPayload } from '@/core/models/auth';
import type { ProfileAddress, UserProfile } from '@/core/models/user';
import type { SignInPayload } from '@/core/schemas/sign-in-schema';
import type { SignUpSchema } from '@/core/schemas/sign-up-schema';
import {
  CHECK_EMAIL_QUERY_KEY,
  RESET_PASSWORD_QUERY_KEY,
  SEND_PASSWORD_RESET_QUERY_KEY,
  SIGNIN_QUERY_KEY,
  SIGNUP_QUERY_KEY,
  USER_PROFILE_QUERY_KEY,
} from '@/core/constants/query-keys';
import {
  checkEmailExists,
  putUserProfileBillingAddress,
  putUserProfileShippingAddress,
  resetPassword,
  sendPasswordReset,
  signIn,
  signInWithIdToken,
  signUp,
  updateUserProfile,
} from '@/core/services/fetchers';

const useProfile = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: signin } = useMutation({
    mutationFn: (data: SignInPayload) => signIn(data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SIGNIN_QUERY_KEY],
      }),
  });

  const { mutateAsync: signinWithIdToken } = useMutation({
    mutationFn: (data: SocialSignInPayload) => signInWithIdToken(data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SIGNIN_QUERY_KEY],
      }),
  });

  const { mutateAsync: signup } = useMutation({
    mutationFn: (data: SignUpSchema) => signUp(data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SIGNUP_QUERY_KEY],
      }),
  });

  const { mutateAsync: checkEmail } = useMutation({
    mutationFn: (email: string) => checkEmailExists(email),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [CHECK_EMAIL_QUERY_KEY],
      }),
  });

  const { mutateAsync: sendPasswordResetEmail } = useMutation({
    mutationFn: ({
      email,
      continueUrl,
      locale,
    }: {
      email: string;
      continueUrl: string;
      locale?: string;
    }) => sendPasswordReset(email, continueUrl, locale),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SEND_PASSWORD_RESET_QUERY_KEY],
      }),
  });

  const { mutateAsync: resetUserPassword } = useMutation({
    mutationFn: ({
      email,
      oobCode,
      newPassword,
    }: {
      email: string;
      oobCode: string;
      newPassword: string;
    }) => resetPassword(email, oobCode, newPassword),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [RESET_PASSWORD_QUERY_KEY],
      }),
  });

  const { mutateAsync: updateProfile } = useMutation<
    void,
    unknown,
    {
      data: Omit<UserProfile, 'EmailAddress'>;
    }
  >({
    mutationFn: ({ data }) => updateUserProfile(data),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [USER_PROFILE_QUERY_KEY],
      }),
  });

  const { mutateAsync: updateProfileAddresses } = useMutation<
    void,
    unknown,
    {
      shipping: ProfileAddress;
      billing: ProfileAddress;
    }
  >({
    mutationFn: async ({ shipping, billing }) => {
      await Promise.all([
        putUserProfileShippingAddress(shipping),
        putUserProfileBillingAddress(billing),
      ]);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [USER_PROFILE_QUERY_KEY],
      }),
  });

  return {
    signin,
    signinWithIdToken,
    signup,
    checkEmail,
    sendPasswordResetEmail,
    resetUserPassword,
    updateProfile,
    updateProfileAddresses,
  };
};

export default useProfile;
